import { supabase } from '@/core/supabase/client';

/**
 * Datos del dashboard.
 *
 * Todo llega de una sola RPC. La razón no es ahorrar viajes: la parte que le
 * toca a cada participante se calcula por mayor resto y depende de las líneas
 * de reparto ajenas, que el propio participante no puede ver (D1). El cálculo
 * tiene que pasar sí o sí por la base. Ver la migración 18.
 *
 * `null` en un importe significa PENDIENTE, nunca cero.
 */

export interface PendingCard {
  count: number;
  pending_count: number;
  /** `null` si algún trabajo del grupo no tiene precio. */
  amount_cents: number | null;
}

export interface DashboardProduction {
  works_count: number;
  total_kg: number;
  pending_count: number;
  honoraria_cents: number | null;
  mobility_cents: number | null;
  total_cents: number | null;
  pending_to_bill: PendingCard;
  pending_to_collect: PendingCard;
}

export interface ParticipantResult {
  participant_id: string;
  display_name: string;
  earned_honoraria_cents: number;
  earned_mobility_cents: number;
  adjustments_cents: number;
  billed_cents: number;
  /** Positivo: le deben. Negativo: debe. */
  balance_cents: number;
}

export type MyResult = Omit<ParticipantResult, 'participant_id' | 'display_name'>;

export interface DashboardSummary {
  organization_id: string;
  year: number;
  month: number;
  is_admin: boolean;
  org_kind: 'solo' | 'team';
  my_participant_id: string | null;
  steer_price_cents: number | null;
  production: DashboardProduction;
  /** `null` cuando el período tiene precios pendientes o el usuario no participa. */
  my_result: MyResult | null;
  /** Sólo para administradores. `null` para el resto. */
  org_result: ParticipantResult[] | null;
}

export async function fetchDashboard(
  organizationId: string,
  year: number,
  month: number,
): Promise<DashboardSummary> {
  const { data, error } = await supabase.rpc('dashboard_summary', {
    p_org: organizationId,
    p_year: year,
    p_month: month,
  });
  if (error) throw error;
  return data as DashboardSummary;
}

// ---------------------------------------------------------------------------
// Detalle económico de un trabajo (D1)
// ---------------------------------------------------------------------------

export interface WorkShare {
  percentage: number | null;
  honoraria_cents: number;
  mobility_cents: number;
  total_cents: number;
}

export interface WorkDetail {
  id: string;
  client_name: string;
  work_date: string;
  billing_year: number;
  billing_month: number;
  is_billed: boolean;
  is_paid: boolean;
  is_locked: boolean;
  total_kg: number;
  honoraria_cents: number | null;
  mobility_cents: number | null;
  total_cents: number | null;
  price_pending: boolean;
  /** El usuario emite el comprobante de este trabajo. */
  is_biller: boolean;
  is_admin: boolean;
  my_share: WorkShare | null;
  /** Agregado sin nombres, para el miembro común. `null` si no hay otros. */
  others?: { percentage: number; honoraria_cents: number | null } | null;
  /** Lista completa con nombres. Sólo llega si el usuario es administrador. */
  splits?: {
    participant_id: string;
    display_name: string;
    percentage: number;
    amount_cents: number | null;
  }[];
}

export async function fetchWorkDetail(workId: string): Promise<WorkDetail> {
  const { data, error } = await supabase.rpc('work_detail', { p_work: workId });
  if (error) throw error;
  return data as WorkDetail;
}
